import { Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { CategoryAnalytics } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { EcommerceBrandSettings } from 'idt-ecommerce-api/config/config.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { MetaTag } from 'shared/models/meta-tags.models';

import { RenderConfigurationService } from './render-configuration.service';


@Injectable({
  providedIn: 'any'
})
export class RenderSeoService {

  public metasSet$: Subject<void> = new Subject();
  public titleSet$: Subject<void> = new Subject();

  private metaTags: MetaTag[] = [];
  private title: string = '';
  private readonly brandSettings: EcommerceBrandSettings | undefined;

  constructor(
    private readonly contextService: ContextService,
    private readonly metaService: Meta,
    private readonly titleService: Title,
    private readonly renderConfigurationService: RenderConfigurationService
  ) {
    this.brandSettings = this.renderConfigurationService.config?.ecommerce?.brandSettings;
  }

  public setPageMetas(metas: MetaTag[]): void {
    this.metaTags = metas;
  }

  public setPageTitle(title: string): void {
    this.title = title;
  }

  public getPageMetas(): MetaTag[] {
    return this.metaTags;
  }

  public getPageTitle(): string {
    return this.title;
  }

  public addPageMetas(categoryId?: string, product?: Product): void {
    this.contextService.getContext$().pipe(
      first()
    ).subscribe((context) => {
      const flattenedCategories = CategoriesHelpers.flatCategoriesMap(context.categories);
      if (flattenedCategories !== undefined && Object.keys(flattenedCategories)?.length > 0) {
        let category: CategoryAnalytics | undefined;
        if (categoryId !== undefined) {
          category = flattenedCategories[categoryId];
        }
        this.removeOldPageMetas();
        this.metaTags.forEach((metaTagData) => {
          let content = metaTagData.content;
          if (this.brandSettings?.obtainParsedSEOText) {
            content = this.brandSettings.obtainParsedSEOText(metaTagData.content, context, product, category);
          }
          const newMeta: MetaDefinition = {};
          if (metaTagData.name !== '') {
            newMeta.name = metaTagData.name;
          }
          if (metaTagData.property) {
            newMeta.property = metaTagData.property;
          }
          newMeta.content = content;
          newMeta.frompage = '';
          if (content.trim() !== '') {
            this.metaService.addTag(newMeta);
          }
        });
        this.metasSet$.next();
      }
    });
  }

  public addPageTitle(categoryId?: string, product?: Product): void {
    this.contextService.getContext$().pipe(
      first()
    ).subscribe((context) => {
      const flattenedCategories = CategoriesHelpers.flatCategoriesMap(context.categories);
      if (flattenedCategories !== undefined && Object.keys(flattenedCategories)?.length > 0) {
        let category: CategoryAnalytics | undefined;
        if (categoryId !== undefined) {
          category = flattenedCategories[categoryId];
        }
        let title = this.title;
        if (this.brandSettings?.obtainParsedSEOText) {
          title = this.brandSettings?.obtainParsedSEOText(this.title, context, product, category);
        }
        if (title !== '') {
          this.titleService.setTitle(title);
        }
        this.titleSet$.next();
      }
    });
  }

  private removeOldPageMetas(): void {
    const oldTags = this.metaService.getTags('frompage');
    oldTags.forEach((meta) => {
      this.metaService.removeTagElement(meta);
    });
  }

}
